import { Link } from "@heroui/link";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

import { parseDate } from "@/lib/utils";
import { getAuditColumns } from "@/pages/a7/Audit/getAuditColumns";

export type A7FIAInstrument = {
  id: string;
  code: string;
  type: string;
  date_de_maturite: string;
  devise: string;
  date_inactivite: string;
  a7_fia_issuer_id?: number;
  a7_fia_issuer_name?: number;
  nom: string;
  contact_fonds: string;
  contact_afd: string;
  compagnie: string;
};

const tableName = "a7_fia_instruments";

export const columnVisibilityInit: VisibilityState = {
  contact_afd: false,
  contact_fonds: false,
  date_inactivite: false,
  compagnie: false,
};

export const getInstrumentsColumns = (
  t: TFunction,
): ColumnDef<A7FIAInstrument>[] => {
  const tTable = (key: string, defaultValue: string) =>
    t(`${tableName}.columns.${key}`, { ns: "tables", defaultValue });
  const tCommon = (key: string, defaultValue: string) =>
    t(`actions.${key}`, { ns: "common", defaultValue });

  return [
    {
      accessorKey: "id",
      header: tTable("id", "ID"),
    },
    {
      accessorKey: "type",
      header: tTable("type", "Instrument type"),
    },
    {
      accessorKey: "devise",
      header: tTable("devise", "Currency"),
    },
    {
      accessorKey: "code",
      header: tTable("code", "Code"),
    },
    {
      accessorKey: "a7_fia_issuer_name",
      header: tTable("a7_fia_issuer_name", "Issuer"),
      cell: ({ row }) => {
        return (
          <Link
            href={toWorkspacePath(
              `/admin/a7-fia/a7_fia_issuers/${row.original.a7_fia_issuer_id}`,
            )}
            className="text-xs"
            underline="hover"
          >
            {row.getValue("a7_fia_issuer_name")}
          </Link>
        );
      },
    },
    {
      accessorKey: "date_de_maturite",
      header: tTable("date_de_maturite", "Maturity date"),
      cell: ({ row }) => (
        <span className="block">
          {parseDate(row.getValue("date_de_maturite"))}
        </span>
      ),
    },
    {
      accessorKey: "nom",
      header: tTable("nom", "Fund name"),
    },
    {
      accessorKey: "contact_fonds",
      header: tTable("contact_fonds", "Fund contact"),
    },
    {
      accessorKey: "contact_afd",
      header: tTable("contact_afd", "AFD contact"),
    },
    {
      accessorKey: "compagnie",
      header: tTable("compagnie", "Company"),
    },
    {
      accessorKey: "date_inactivite",
      header: () => (
        <div className="text-center">
          {tTable("date_inactivite", "Inactive date")}
        </div>
      ),
      cell: ({ row }) => (
        <span className="block">
          {parseDate(row.getValue("date_inactivite"))}
        </span>
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => {
        return (
          <div className="flex justify-end">
            <Link
              href={`${window.location.pathname}/${row.getValue("id")}`}
              className="text-xs"
              underline="hover"
            >
              {tCommon("edit", "Edit")}
            </Link>
          </div>
        );
      },
    },
  ];
};

export const getInstrumentAuditColumns = (t: TFunction) =>
  getAuditColumns<A7FIAInstrument>([
    ...getInstrumentsColumns(t),
    {
      accessorKey: "a7_fia_issuer_id",
      header: t(`${tableName}.columns.a7_fia_issuer_id`, {
        ns: "tables",
        defaultValue: "Issuer ID",
      }),
    },
  ]).filter((column) => {
    const accessorKey =
      "accessorKey" in column
        ? String((column as { accessorKey?: unknown }).accessorKey ?? "")
        : "";
    return !["a7_fia_issuer_name", "id"].includes(accessorKey);
  });
